import { loadStripe } from '@stripe/stripe-js';
import type { BillingStatus } from '../types';
import { supabase } from './supabase';

const stripePromise = (() => {
  const key = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
  return key ? loadStripe(key) : Promise.resolve(null);
})();


async function getAccessToken() {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token || null;
}

async function authedFetch(path: string, init: RequestInit = {}) {
  const token = await getAccessToken();
  const headers = new Headers(init.headers || {});
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  const response = await fetch(path, { ...init, headers });
  const isJson = response.headers.get('content-type')?.includes('application/json');
  const payload = isJson ? await response.json() : null;
  if (!response.ok) {
    throw new Error(payload?.error || 'Request failed');
  }
  return payload;
}

export async function fetchBillingStatus(): Promise<BillingStatus> {
  return authedFetch('/.netlify/functions/billing-status');
}

export async function startCheckout(priceId: string) {
  const payload = await authedFetch('/.netlify/functions/create-checkout-session', {
    method: 'POST',
    body: JSON.stringify({ priceId }),
  });

  if (payload?.url) {
    window.location.href = payload.url;
    return;
  }

  if (!payload?.sessionId) throw new Error('Stripe checkout session was not created.');
  const stripe = await stripePromise;
  if (!stripe) throw new Error('Stripe is not configured.');
  const result = await stripe.redirectToCheckout({ sessionId: payload.sessionId });
  if (result.error) throw new Error(result.error.message || 'Could not redirect to Stripe Checkout.');
}

export async function openCustomerPortal() {
  const payload = await authedFetch('/.netlify/functions/create-portal-session', { method: 'POST' });
  if (!payload?.url) throw new Error('Stripe customer portal URL was not returned.');
  window.location.href = payload.url;
}
